import { useState, useMemo } from "react";
import { OFFENCE_CLASSIFICATION } from "@/lib/offenceClassification";
import { Search, X, ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * @param {{
 *   value?: string[],
 *   onChange: (next: string[]) => void
 * }} props
 */
export default function ChargeSelector({ value = [], onChange }) {
  const [search, setSearch] = useState("");
  const [openHeadings, setOpenHeadings] = useState({});

  /** @param {string} heading */
  const toggleHeading = (heading) =>
    setOpenHeadings((prev) => ({ ...prev, [heading]: !prev[heading] }));

  /** @param {string} charge */
  const toggle = (charge) => {
    if (value.includes(charge)) {
      onChange(value.filter((v) => v !== charge));
    } else {
      onChange([...value, charge]);
    }
  };

  // Search matches a charge or its heading
  const searchResults = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    /** @type {Record<string, string[]>} */
    const results = {};
    for (const [heading, charges] of Object.entries(OFFENCE_CLASSIFICATION)) {
      const matched = heading.toLowerCase().includes(q)
        ? charges
        : charges.filter((c) => c.toLowerCase().includes(q));
      if (matched.length > 0) results[heading] = matched;
    }
    return results;
  }, [search]);

  const isSearching = search.trim().length > 0;

  /** @param {string[]} charges */
  const countSelected = (charges) => charges.filter((c) => value.includes(c)).length;

  const data = isSearching ? searchResults : OFFENCE_CLASSIFICATION;

  return (
    <div className="space-y-2">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search charges..."
          className="pl-9"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Selected pills */}
      {value.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 bg-white border border-purple-300 text-purple-800 text-xs px-2 py-0.5 rounded-full"
            >
              {v}
              <button
                onClick={() => onChange(value.filter((x) => x !== v))}
                className="text-purple-400 hover:text-purple-700"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Tree */}
      <div className="border border-slate-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
        {!data || Object.keys(data).length === 0 ? (
          <p className="p-4 text-sm text-slate-400 text-center">No results found</p>
        ) : (
          Object.entries(data).map(([heading, charges]) => {
            const selectedCount = countSelected(charges);
            const open = isSearching || openHeadings[heading];
            return (
              <div key={heading} className="border-b border-slate-100 last:border-b-0">
                {/* Heading */}
                <button
                  type="button"
                  onClick={() => toggleHeading(heading)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-100 hover:bg-slate-200 transition-colors text-left"
                >
                  <div className="flex items-center gap-2">
                    {open ? (
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    )}
                    <span className="text-sm font-bold text-slate-800">{heading}</span>
                    {selectedCount > 0 && (
                      <span className="text-xs bg-purple-600 text-white font-bold px-1.5 py-0.5 rounded-full">
                        {selectedCount}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-slate-400">{charges.length}</span>
                </button>

                {open &&
                  charges.map((charge) => {
                    const isChecked = value.includes(charge);
                    return (
                      <label
                        key={charge}
                        className={`flex items-center gap-3 pl-10 pr-4 py-1.5 cursor-pointer hover:bg-slate-50 transition-colors ${isChecked ? "bg-purple-50" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggle(charge)}
                          className="w-4 h-4 accent-purple-600 flex-shrink-0"
                        />
                        <span className="text-sm text-slate-800">{charge}</span>
                      </label>
                    );
                  })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
